import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { HighlightText } from "@/components/highlight-text"
import { slideInFrom, sectionHeaderClasses } from "@/lib/section-slide"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const competitiveAdvantages = [
  "Deep domain expertise across nodes and applications",
  "Flexible staffing that scales with your roadmap",
  "Lower blended cost than in-house hiring",
  "Faster ramp — no recruiting lead time",
  "Robust quality bar backed by senior review",
]

const businessModels = [
  "Dedicated ODC on your IT and tools",
  "Managed, fixed-scope projects",
  "Long-term strategic partnerships",
]

export function DifferentiateSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const accentLineRef = useRef<HTMLDivElement>(null)
  const leftColRef = useRef<HTMLDivElement>(null)
  const rightColRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !boxRef.current) return

    const ctx = gsap.context(() => {
      slideInFrom(headerRef.current, "right")
      slideInFrom(boxRef.current, "right", { delay: 0.1 })
      slideInFrom(leftColRef.current, "left", { delay: 0.2 })
      slideInFrom(rightColRef.current, "right", { delay: 0.25 })

      if (accentLineRef.current) {
        gsap.fromTo(
          accentLineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 1,
            ease: "power3.inOut",
            scrollTrigger: {
              trigger: boxRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="differentiate" className="relative border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12">
      <div ref={headerRef} className={cn("mb-16 max-w-4xl", sectionHeaderClasses("right"))}>
        <span className="mb-4 block font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Differentiate
        </span>
        <h2 className="mt-4 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-7xl">
          <HighlightText>Expert Team.</HighlightText> Faster Ramp.
          <br />
          Same Rigor.
        </h2>
      </div>

      <div ref={boxRef} className="grid border border-border/30 md:grid-cols-2">
        <div ref={leftColRef} className="border-b border-border/30 p-8 md:border-b-0 md:border-r md:p-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Competitive Advantage
          </span>

          <ul className="mt-8 space-y-5">
            {competitiveAdvantages.map((item) => (
              <li key={item} data-diff-item className="flex gap-4 font-mono text-xs leading-relaxed text-foreground/90 md:text-sm">
                <span className="mt-1 shrink-0 font-mono text-accent" aria-hidden="true">
                  —
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div ref={rightColRef} className="relative p-8 md:p-12">
          <div className="absolute inset-x-0 top-0 h-1 overflow-hidden bg-border/30">
            <div ref={accentLineRef} className="h-full w-full origin-left scale-x-0 bg-accent" />
          </div>

          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Business Model</span>

          <ul className="mt-8 space-y-5">
            {businessModels.map((item) => (
              <li key={item} data-diff-item className="flex gap-4 font-mono text-xs leading-relaxed text-foreground/90 md:text-sm">
                <span className="mt-1 shrink-0 font-mono text-accent" aria-hidden="true">
                  —
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
